import { LanguageType, UnityDetail } from '@/custom-types'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Col, Row } from 'react-grid-system'
import textsData from '@/textData/AddUnitFormTexts.json'
import { useUserLanguage } from '@/customHooks'
import { BedSizeOption, UnityStatusOption } from '@/graphql/generated'
import {
  NormalCheckBoxField,
  NormalFormSelectionField,
  NormalFormTextField,
} from '../molecules'
import { bedSizeMapper } from '@/helpers'

type AddUnityFormProps = {
  defaultUnityData: UnityDetail | null
  onChange: (unitData: UnityDetail) => void
}

const emptyData: UnityDetail = {
  id: '',
  name: '',
  currentStatusOption: UnityStatusOption.Vacant,
  bedSizeOption: BedSizeOption.Twin,
  privateBathroom: false,
  airConditioner: false,
  fan: false,
  contracts: [],
}

const bedSizes = (userLanguage: LanguageType) => {
  return [
    BedSizeOption.Twin,
    BedSizeOption.TwinXl,
    BedSizeOption.Double,
    BedSizeOption.Queen,
    BedSizeOption.King,
  ].map((bedSize) => ({
    name: bedSizeMapper(bedSize, userLanguage),
    value: bedSize,
  }))
}

export const  AddUnitFormControlled = memo(({ defaultUnityData, onChange }: AddUnityFormProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [initialUnityData, setUnityData] = useState<UnityDetail>(defaultUnityData || emptyData)
  const unityDataRef = useRef<UnityDetail>(defaultUnityData || emptyData)

  useEffect(() => {
    if (!defaultUnityData) return

    unityDataRef.current = defaultUnityData
    setUnityData(defaultUnityData)
  }, [defaultUnityData])

  const onFieldChangeHandle = useCallback((field: keyof UnityDetail, value: string | boolean | null) => {
    if (value === null) return

    unityDataRef.current = { ...unityDataRef.current, [field]: value }
    
    /* 
    if (typeof value === 'boolean') {
      unityDataRef.current = { ...unityDataRef.current, [field]: value }
    } else if (typeof value === 'string') {
      unityDataRef.current = { ...unityDataRef.current, [field]: value }
    }
     */
    onChange(unityDataRef.current)
  }, [onChange])

  const options = [
    { name: texts.status.options.occupied, value: UnityStatusOption.Occupied },
    { name: texts.status.options.vacant, value: UnityStatusOption.Vacant },
  ]

  return (
    <>
      <Row>
        <Col md={8}>
          <NormalFormTextField
            name="name"
            label={texts.unitName.label}
            value={initialUnityData.name}
            placeholder={texts.unitName.placeholder}
            onChange={(value) => onFieldChangeHandle('name', value)}
          />
        </Col>
        <Col md={4}>
          <NormalFormSelectionField<UnityStatusOption>
            options={options}
            name="status"
            value={initialUnityData.currentStatusOption}
            label={texts.status.label}
            onChange={(value) => onFieldChangeHandle('currentStatusOption', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <NormalFormSelectionField<BedSizeOption>
            name="bedSize"
            label={texts.beds}
            options={bedSizes(userLanguage)}
            onChange={(value) => onFieldChangeHandle('bedSizeOption', value)}
            value={BedSizeOption.Twin}
          />
          {/* <NormalFormNumberField
            name="bedrooms"
            label={texts.beds.label}
            placeholder={texts.beds.label}
            value={initialUnityData.bedrooms.toString()}
            onChange={(value) => onFieldChangeHandle('bedrooms', value)}
          /> */}
        </Col>
        <Col md={2}>
          <NormalCheckBoxField
            name="privateBath"
            label={texts.bath}
            onChange={(value) => onFieldChangeHandle('privateBathroom', value)}
            defaultChecked={initialUnityData.privateBathroom}
          />
          {/* <NormalFormNumberField
            name="bathrooms"
            label={texts.baths.label}
            placeholder={texts.baths.label}
            value={initialUnityData.bathrooms.toString()}
            onChange={(value) => onFieldChangeHandle('bathrooms', value)}
          /> */}
        </Col>
        <Col md={2}>
          <NormalCheckBoxField
            name="airConditioner"
            label={texts.airConditioner}
            onChange={(value) => onFieldChangeHandle('airConditioner', value)}
            defaultChecked={initialUnityData.airConditioner}
          />
        </Col>
        <Col md={2}>
          <NormalCheckBoxField
            name="fan"
            label={texts.fan}
            onChange={(value) => onFieldChangeHandle('fan', value)}
            defaultChecked={initialUnityData.fan}
          />
        </Col>
        {/* <Col md={4}>
          <NormalFormNumberField
            name="size"
            label={texts.size.label}
            value={initialUnityData.size.toString()}
            onChange={(value) => onFieldChangeHandle('size', value)}
          />
        </Col> */}
      </Row>
    </>
  )
})
